import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  DrawerBody,
  DrawerFooter,
  DrawerHeader,
  DrawerHeaderTitle,
  Field,
  Input,
  OverlayDrawer,
  Option,
  Dropdown,
  Spinner,
  Switch,
  Text,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { MessageBarType } from '@fluentui/react';
import { MessageComponent } from '../../../../commons/component/MessageComponent';
import { connector, Props, useStyles } from './QueueDisplayForm.Functions';
import { QueueDisplayScreen } from '../../../../models/Entities/Turnero';

const QueueDisplayFormComponent: React.FC<Props> = ({
  isOpen,
  onClose,
  displayId,
  defaultSectorId,
  defaultSedeId,
  isSaving,
  isSaved,
  failOnSaving,
  error,
  currentDisplay,
  sectores,
  sedes,
  isLoadingSectores,
  isLoadingSedes,
  CreateQueueDisplayScreen,
  UpdateQueueDisplayScreen,
  GetQueueDisplayScreens,
  GetSectores,
  GetSedes,
}) => {
  const styles = useStyles();

  const [form, setForm] = useState<QueueDisplayScreen>({
    description: '',
    name: '',
    fingerPrint: '',
    registrationNumber: '',
    isActive: true,
    sectorId: defaultSectorId,
    sedeId: defaultSedeId,
  });
  const [submitted, setSubmitted] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const isEditing = useMemo(() => Boolean(displayId), [displayId]);

  const resetForm = () => {
    setForm({
      description: '',
      name: '',
      fingerPrint: '',
      registrationNumber: '',
      isActive: true,
      sectorId: defaultSectorId,
      sedeId: defaultSedeId,
    });
    setSubmitted(false);
    setValidationErrors([]);
  };

  // Load lookup data when needed
  useEffect(() => {
    const needSectores = !sectores?.length && !isLoadingSectores;
    const needSedes = !sedes?.length && !isLoadingSedes;
    if (!isOpen && !(needSectores || needSedes)) return;
    //if (needSectores) GetSectores();
    //if (needSedes) GetSedes();
  }, [isOpen, GetSectores, GetSedes, sectores?.length, sedes?.length, isLoadingSectores, isLoadingSedes]);

  // Ensure we have the item when editing
  useEffect(() => {
    if (isOpen && isEditing && !currentDisplay) {
      GetQueueDisplayScreens();
    }
  }, [isOpen, isEditing, currentDisplay, GetQueueDisplayScreens]);

  // Hydrate form when editing
  useEffect(() => {
    if (isEditing && currentDisplay) {
      setForm({
        ...currentDisplay,
        description: currentDisplay.description || '',
        name: currentDisplay.name || '',
        fingerPrint: currentDisplay.fingerPrint || currentDisplay.fingerprint || '',
        registrationNumber: currentDisplay.registrationNumber || '',
        isActive: currentDisplay.isActive ?? true,
        sectorId: currentDisplay.sectorId ?? undefined,
        sedeId: currentDisplay.sedeId ?? undefined,
      });
      setSubmitted(false);
      setValidationErrors([]);
    }
  }, [isEditing, currentDisplay]);

  // Reset when closing or defaults change (create mode)
  useEffect(() => {
    if (!isOpen) {
      resetForm();
    } else if (!isEditing) {
      setForm((prev) => ({
        ...prev,
        sectorId: defaultSectorId,
        sedeId: defaultSedeId,
      }));
    }
  }, [isOpen, isEditing, defaultSectorId, defaultSedeId]);

  // Close after successful save
  useEffect(() => {
    if (isSaved && submitted) {
      GetQueueDisplayScreens();
      resetForm();
      onClose();
    }
  }, [GetQueueDisplayScreens, isSaved, submitted, onClose]);

  const onChange = (key: keyof QueueDisplayScreen, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const validate = () => {
    const errors: string[] = [];
    if (!form.name || !form.name.trim()) {
      errors.push('Debe ingresar un nombre para el display');
    }
    setValidationErrors(errors);
    return errors.length === 0;
  };

  const onSubmit = () => {
    setSubmitted(true);
    if (!validate()) return;

    const payload: QueueDisplayScreen = {
      name: form.name?.trim() || null,
      description: form.description?.trim() || null,
      registrationNumber: form.registrationNumber?.trim() || null,
      fingerPrint: form.fingerPrint?.trim() || null,
      sectorId: form.sectorId ?? null,
      sedeId: form.sedeId ?? null,
      isActive: form.isActive ?? true,
    };

    if (isEditing && displayId) {
      UpdateQueueDisplayScreen(displayId, { ...payload, id: displayId });
    } else {
      CreateQueueDisplayScreen(payload);
    }
  };

  return (
    <OverlayDrawer position="end" open={isOpen}>
      <DrawerHeader>
        <DrawerHeaderTitle
          action={
            <Button
              appearance="subtle"
              aria-label="Close"
              icon={<Dismiss24Regular />}
              onClick={onClose}
              disabled={isSaving}
            />
          }
        >
          {isEditing ? 'Editar display' : 'Nuevo display'}
        </DrawerHeaderTitle>
      </DrawerHeader>

      <DrawerBody>
        <div className={styles.container}>
          {validationErrors.length > 0 && (
            <MessageComponent
              message="Verifique la información suministrada"
              type={MessageBarType.error}
              itemList={validationErrors}
            />
          )}
          {failOnSaving === true && (
            <MessageComponent
              message={error?.ErrorMessage || 'Ocurrió un error inesperado'}
              type={MessageBarType.error}
              itemList={error?.Errors}
            />
          )}
          <div className={styles.fieldGroup}>
            <Field label="Nombre" required>
              <Input value={form.name || ''} onChange={(_, d) => onChange('name', d.value)} placeholder="Nombre del display" />
            </Field>
            <Field label="Descripción">
              <Input
                value={form.description || ''}
                onChange={(_, d) => onChange('description', d.value)}
                placeholder="Descripción (opcional)"
              />
            </Field>
          </div>
          <div className={styles.fieldGroup}>
            <Field label="Número de registro">
              <Input
                value={form.registrationNumber || ''}
                onChange={(_, d) => onChange('registrationNumber', d.value)}
                placeholder="Número de registro (opcional)"
              />
            </Field>
            <Field label="Huella">
              <Input
                value={form.fingerPrint || ''}
                onChange={(_, d) => onChange('fingerPrint', d.value)}
                placeholder="Huella (opcional)"
              />
            </Field>
          </div>
          <div className={styles.fieldGroup}>
            <Field label="Sede">
              <Dropdown
                placeholder="Seleccione una sede"
                selectedOptions={form.sedeId !== undefined && form.sedeId !== null ? [String(form.sedeId)] : []}
                onOptionSelect={(_, data) => onChange('sedeId', data.optionValue ? Number(data.optionValue) : undefined)}
              >
                {sedes.map((sede) => {
                  const label = sede.name || `Sede ${sede.id ?? ''}`;
                  return (
                    <Option key={sede.id} value={String(sede.id)} text={label}>
                      {label}
                    </Option>
                  );
                })}
              </Dropdown>
            </Field>
            <Field label="Sector">
              <Dropdown
                placeholder="Seleccione un sector"
                selectedOptions={form.sectorId !== undefined && form.sectorId !== null ? [String(form.sectorId)] : []}
                onOptionSelect={(_, data) => onChange('sectorId', data.optionValue ? Number(data.optionValue) : undefined)}
              >
                {sectores.map((sector) => {
                  const label = sector.name || `Sector ${sector.id ?? ''}`;
                  return (
                    <Option key={sector.id} value={String(sector.id)} text={label}>
                      {label}
                    </Option>
                  );
                })}
              </Dropdown>
            </Field>
          </div>
          <Field label="Activa">
            <Switch
              checked={form.isActive ?? true}
              onChange={(_, data) => onChange('isActive', data.checked)}
              label={form.isActive ? 'Sí' : 'No'}
            />
          </Field>
        </div>
      </DrawerBody>

      <DrawerFooter>
        <div className={styles.actions}>
          <Button onClick={onClose} disabled={isSaving}>
            Cancelar
          </Button>
          <Button appearance="primary" onClick={onSubmit} disabled={isSaving}>
            {isSaving && (
              <>
                <Spinner size="extra-tiny" /> <Text>Guardando</Text>
              </>
            )}
            {!isSaving && (isEditing ? 'Actualizar' : 'Crear')}
          </Button>
        </div>
      </DrawerFooter>
    </OverlayDrawer>
  );
};

export default connector(QueueDisplayFormComponent as any);
